import { StatusCodes } from '../Libs/websocket-status-codes'
import { StatusCodes as HTTPStatusCodes } from '../Libs/http-status-codes'
import { MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useRelogin } from './use-relogin'
import { useDebounce } from './useDebounce'
import { stringifyError, useNewRelic } from '../Libs/observability-tool'
import { AuthContext } from '../Libs/authentication'
import { secondsToMilliseconds } from 'date-fns'
import { isIOS } from 'react-device-detect'

const TRY_AGAIN_LATER_RETRIES_AMOUNT = 3
const POLICY_VIOLATION_RETRIES_AMOUNT = 1

export interface SocketStateModel<M> {
  connected: boolean
  error?: {
    code: StatusCodes
    reason?: string
  }
  data?: M
}

interface Props<M, D> {
  /** The to be called api resource */
  apiResourcePath: string
  /** Identifier for this websocket instance during the logging */
  loggerKey: string
  /** A function to transform the incoming api resource object into a local domain model, recognized by the application */
  transformer: (dto: D) => M
  /** The to be called api endpoint (default REACT_APP_API_ENDPOINT endpoint */
  endpoint: string
  /** A parameter to when not to initialize the websocket */
  doNotInitialize?: boolean
  /** Dependencies to reopen the websocket */
  dependencies?: Array<unknown>
  /** Do not close websocket of errors, retry after 15 seconds and keep that alive */
  keepAlive?: boolean
}

interface UseWebSocketApiReturnType<M> {
  socketState: SocketStateModel<M>
  webSocket: MutableRefObject<WebSocket | null>
  resetWebsocketToInitial: () => void
}

/**
 * A hook to connect to one of our existing websockets.
 *
 * Closes itself as soon as the components unmounts where the hook was called.
 * Reopens with reauthentication on jwt and websocket expiration codes according to https://docs.e3dc.com/x/J4ZmB
 *
 * @param {Props.apiResourcePaht} apiResourcePath The to be called api resource
 * @param transformer A function to transform the incoming api resource object into a local domain model, recognized by the application
 * @param loggerKey Identifier for this websocket instance during the logging
 * @param endpoint The to be called api endpoint (default REACT_APP_API_ENDPOINT endpoint
 * @template M - Local domain model, recognized by the application
 * @template D - Incoming api resource object
 */
export const useWebSocketApi = <M, D>({
  apiResourcePath,
  loggerKey,
  transformer,
  endpoint,
  dependencies = [],
  doNotInitialize = false,
  keepAlive = false,
}: Props<M, D>): UseWebSocketApiReturnType<M> => {
  const [socketState, setSocketState] = useState<SocketStateModel<M>>({ connected: false })
  const [retryCounter, setRetryCounter] = useState(0)
  const { authToken } = useContext(AuthContext)
  const webSocket = useRef<WebSocket | null>(null)
  const debouncedRetryCounter = useDebounce(retryCounter, keepAlive ? 15000 : 100)
  const relogin = useRelogin()
  const { pageActions } = useNewRelic()
  const websocketEndpoint = useMemo(() => endpoint.replace('https://', 'wss://').replace('http://', 'ws://'), [endpoint])
  /** Timestamp of the last activity (open or incoming message) used by the heartbeat to detect dead connections. */
  const lastMessageAt = useRef<number>(Date.now())

  const resetWebsocketToInitial = useCallback(() => {
    webSocket.current?.close()
    setSocketState({ connected: false })
    setRetryCounter(0)
    webSocket.current = null
  }, [webSocket, setSocketState, setRetryCounter])

  useForceSocketReconnectForIOS(doNotInitialize, loggerKey, lastMessageAt, webSocket, setRetryCounter, relogin)

  useEffect(() => {
    if (doNotInitialize || !authToken) return

    webSocket.current = new WebSocket(`${websocketEndpoint}/${apiResourcePath}?authorization=${authToken}`)

    webSocket.current.onopen = _ => {
      console.log(`Websocket ${loggerKey} opened.`)
      lastMessageAt.current = Date.now()
      setSocketState({ connected: true, error: undefined, data: undefined })
    }

    webSocket.current.onmessage = event => {
      lastMessageAt.current = Date.now()
      const data = JSON.parse(event.data)

      if (data.statusCode === HTTPStatusCodes.NO_CONTENT && Object.keys(data).length === 1) {
        setSocketState(state => ({ ...state, lastEvent: undefined }))
        return
      }

      const model = transformer(data) as M

      setSocketState(state => ({ ...state, data: model }))
    }

    webSocket.current.onerror = _ => {
      setSocketState(state => ({ ...state, error: { code: StatusCodes.CLOSE_ABNORMAL } }))
      console.error(`Websocket ${loggerKey} had an unknown error.`)
    }

    webSocket.current.onclose = async event => {
      console.log(`Websocket ${loggerKey} was closed with code "${event.code}" and reason "${event.reason}".`)

      setSocketState(state => ({ ...state, connected: false, error: { code: event.code, reason: event.reason } }))
      const errorHasChanged = socketState.error?.code !== event.code

      switch (event.code) {
        case StatusCodes.TRY_AGAIN_LATER: {
          if (debouncedRetryCounter < TRY_AGAIN_LATER_RETRIES_AMOUNT || errorHasChanged) {
            console.log(`Retrying to open websocket ${loggerKey}`)
            setRetryCounter(state => (errorHasChanged ? 1 : state + 1))
          }
          break
        }
        case StatusCodes.POLICY_VIOLATION: {
          if (debouncedRetryCounter < POLICY_VIOLATION_RETRIES_AMOUNT || errorHasChanged) {
            console.log(`Retrying to open websocket ${loggerKey}`)
            try {
              await relogin()
            } catch (err) {
              pageActions.logEvent({ logValue: `Relogin-Error on use-websocket-api: ${stringifyError(err as Error)}` })
            }
            setRetryCounter(state => (errorHasChanged ? 1 : state + 1))
          }
          break
        }
        default:
          if (keepAlive) {
            console.log(`Retrying to open websocket ${loggerKey}`)
            setRetryCounter(state => (errorHasChanged ? 1 : state + 1))
          }
      }
    }

    return () => {
      console.log(`${loggerKey} closing...`)
      webSocket?.current?.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, loggerKey, apiResourcePath, debouncedRetryCounter, relogin, websocketEndpoint, transformer, ...dependencies, doNotInitialize])

  return { socketState, webSocket, resetWebsocketToInitial }
}

/** How often the heartbeat checks whether the connection needs to be revived. */
const HEARTBEAT_INTERVAL_MS = secondsToMilliseconds(5)
/**
 * If the gap between two heartbeat ticks exceeds the interval by more than this,
 * the JS timers must have been frozen - i.e. the app/tab was backgrounded and
 * has just resumed - so the socket is treated as a zombie and reconnected.
 */
const RESUME_DRIFT_THRESHOLD_MS = secondsToMilliseconds(5)
/**
 * Backstop: if the socket claims to be connected but no message has arrived for
 * this long, treat it as dead and reconnect. Kept well above the normal live
 * data cadence (1-3 messages per minute) to avoid false positives.
 */
const STALE_CONNECTION_THRESHOLD_MS = secondsToMilliseconds(45)

/**
 * iOS (Safari and the WebView used by the mobile app) suspends WebSocket
 * connections when the app/tab is backgrounded. The connection is often left
 * as a "zombie" - its `readyState` may still report OPEN while no messages are
 * ever delivered again, and `onclose` is frequently never fired. When the app
 * returns to the foreground nothing re-triggers the connect effect below, so
 * the live data stays frozen until the auth token happens to refresh.
 */
const useForceSocketReconnectForIOS = (
  doNotInitialize: boolean,
  loggerKey: string,
  lastMessageAt: MutableRefObject<number>,
  webSocket: MutableRefObject<WebSocket | null>,
  setRetryCounter: React.Dispatch<React.SetStateAction<number>>,
  relogin: () => Promise<string>
): void => {
  useEffect(() => {
    if (doNotInitialize || !isIOS) return

    let lastTick = Date.now()

    const reconnect = async (): Promise<void> => {
      console.log(`Websocket ${loggerKey} reconnecting after detecting a stale/suspended connection.`)
      lastMessageAt.current = Date.now()
      webSocket.current?.close()
      // A long suspension (e.g. the app was backgrounded for several minutes)
      // can outlast the auth token. While backgrounded nothing refreshes it, so
      // reopening the socket with the stale token silently fails and the data
      // stays frozen. Refresh the token first - the resulting authToken change
      // re-triggers the connect effect; the retry counter bump is a fallback for
      // when the token is still valid.
      try {
        await relogin()
      } catch (error) {
        console.error(`Websocket ${loggerKey} could not refresh the auth token before reconnecting.`, error)
      }
      setRetryCounter(state => state + 1)
    }

    // The heartbeat only re-checks every HEARTBEAT_INTERVAL_MS, so on its own it
    // can take a few seconds after the app returns to the foreground to revive a
    // suspended socket. These DOM events fire the moment the tab/app becomes
    // visible again, letting us reconnect immediately if the connection looks
    // stale. They are unreliable inside a backgrounded native WebView (hence the
    // heartbeat above), but on plain Safari/desktop they are the fastest signal.
    const handleForeground = (): void => {
      if (document.visibilityState === 'hidden') return

      // Reset drift tracking so the next heartbeat tick does not also count the
      // background gap and trigger a second redundant reconnect.
      lastTick = Date.now()

      const isStale = Date.now() - lastMessageAt.current > STALE_CONNECTION_THRESHOLD_MS

      if (isStale) reconnect()
    }

    const intervalId = setInterval(() => {
      const now = Date.now()
      const drift = now - lastTick - HEARTBEAT_INTERVAL_MS
      lastTick = now

      const wasSuspended = drift > RESUME_DRIFT_THRESHOLD_MS
      const isStale = now - lastMessageAt.current > STALE_CONNECTION_THRESHOLD_MS

      if (wasSuspended || isStale) reconnect()
    }, HEARTBEAT_INTERVAL_MS)

    document.addEventListener('visibilitychange', handleForeground)
    window.addEventListener('focus', handleForeground)
    window.addEventListener('pageshow', handleForeground)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleForeground)
      window.removeEventListener('focus', handleForeground)
      window.removeEventListener('pageshow', handleForeground)
    }
  }, [loggerKey, doNotInitialize, webSocket, lastMessageAt, setRetryCounter, relogin])
}
